// State management for CTM Chat Widget
export class StateManager {
  // Constants
  static SESSION_COOKIE_NAME = '_ccs_';

  constructor(logger) {
    this.logger = logger || console;
    // Initialize all state properties
    this.state = {
      // UI State
      isOpen: false,
      keyboardOpen: false,
      ready: false,
      
      // Connection State
      connectionStatus: 'disconnected', // disconnected, connecting, connected, error, closed, ended
      initialConnectionAttempted: false,
      lazyConnectionReady: false,
      
      // Session State
      sessionId: null,
      sessionAssignmentReceived: false,
      sessionToken: null,
      sessionTokenExpiry: null,
      durableObjectId: null,
      
      // Chat State
      aiEnabled: false,
      waitingForAgent: false,
      callId: null,
      didEndChat: false,
      
      // Feature State
      attachmentsEnabled: false,
      
      // Schedule Visibility State
      scheduleVisibility: {
        isVisible: true,
        hasSchedule: false,
        nextTransition: null,
        checkInterval: null
      },

      // Agent Availability State
      // Map of queue_id -> { available, count, total, unavailable_since, updated_at }
      // or simple object for default queue availability
      agentAvailability: {}
    };
    
    // State change listeners
    this.listeners = new Map();
  }

  // Get a state value
  get(key) {
    const keys = key.split('.');
    let value = this.state;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }
    
    return value;
  }

  // Set a state value and notify listeners
  set(key, value) {
    const keys = key.split('.');
    const lastKey = keys.pop();
    let target = this.state;
    
    // Navigate to the parent object
    for (const k of keys) {
      if (!target[k] || typeof target[k] !== 'object') {
        target[k] = {};
      }
      target = target[k];
    }
    
    // Store old value for comparison
    const oldValue = target[lastKey];
    
    // Skip if value hasn't changed
    if (oldValue === value) {
      return;
    }
    
    // Update the value
    target[lastKey] = value;
    
    // Notify listeners
    this.notifyListeners(key, value, oldValue);
  }

  // Update multiple state values at once
  update(updates) {
    Object.entries(updates).forEach(([key, value]) => {
      this.set(key, value);
    });
  }

  // Subscribe to state changes
  subscribe(key, callback) {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key).add(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }

  // Notify listeners of state changes
  notifyListeners(key, newValue, oldValue) {
    // Notify specific key listeners
    const callbacks = this.listeners.get(key);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(newValue, oldValue, key);
        } catch (error) {
          this.logger.error(`Error in state listener for ${key}:`, error);
        }
      });
    }
    
    // Notify wildcard listeners (listening to parent paths)
    const keyParts = key.split('.');
    for (let i = keyParts.length - 1; i > 0; i--) {
      const parentKey = keyParts.slice(0, i).join('.');
      const wildcardKey = parentKey + '.*';
      const wildcardCallbacks = this.listeners.get(wildcardKey);
      
      if (wildcardCallbacks) {
        wildcardCallbacks.forEach(callback => {
          try {
            callback(newValue, oldValue, key);
          } catch (error) {
            this.logger.error(`Error in wildcard state listener for ${wildcardKey}:`, error);
          }
        });
      }
    }
  }

  // Helper methods for common state operations
  
  // Connection status helpers
  isConnected() {
    return this.state.connectionStatus === 'connected';
  }

  isConnecting() {
    return this.state.connectionStatus === 'connecting';
  }

  isDisconnected() {
    return this.state.connectionStatus === 'disconnected' || 
           this.state.connectionStatus === 'closed' ||
           this.state.connectionStatus === 'error';
  }

  isChatEnded() {
    return this.state.connectionStatus === 'ended';
  }

  // Session helpers
  hasSession() {
    return !!this.state.sessionId;
  }

  hasActiveCall() {
    return !!this.state.callId;
  }

  // Feature state helpers
  canSendMessages() {
    return this.isConnected() && 
           this.state.aiEnabled && 
           !this.state.waitingForAgent &&
           !this.isChatEnded();
  }

  canShowSuggestions() {
    return !this.hasActiveCall() || 
           this.state.waitingForAgent;
  }

  // Schedule visibility helpers
  isWidgetVisible() {
    return this.state.scheduleVisibility.isVisible;
  }

  hasSchedule() {
    return this.state.scheduleVisibility.hasSchedule;
  }

  // Agent availability helpers
  getAgentAvailability(queueId = null) {
    const availability = this.state.agentAvailability;
    if (!availability || Object.keys(availability).length === 0) {
      return { available: true }; // Default to available if no data
    }
    if (queueId && availability[queueId]) {
      return availability[queueId];
    }
    // If no specific queue requested, return first available or default
    const firstQueue = Object.values(availability)[0];
    return firstQueue || { available: true };
  }

  isAgentAvailable(queueId = null) {
    return this.getAgentAvailability(queueId).available !== false;
  }

  setAgentAvailability(availability) {
    this.set('agentAvailability', availability);
  }

  // Reset state to initial values
  reset() {
    this.update({
      isOpen: false,
      keyboardOpen: false,
      connectionStatus: 'disconnected',
      lazyConnectionReady: false,
      sessionId: null,
      sessionAssignmentReceived: false,
      sessionToken: null,
      sessionTokenExpiry: null,
      durableObjectId: null,
      aiEnabled: false,
      waitingForAgent: false,
      callId: null,
      didEndChat: false,
      attachmentsEnabled: false
    });
  }

  // Get current state snapshot
  getSnapshot() {
    return JSON.parse(JSON.stringify(this.state));
  }

  // Debug helper
  debug() {
    this.logger.debug('Current Widget State:', this.getSnapshot());
  }
}
